import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { galText } from "./accounts";
import * as api from "./api";
import { callDay, callTime, emailName, shortTime } from "./calls";
import { go, useTitle } from "./hooks";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { ORDER_COLUMNS } from "./columns";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import {
  cancelError,
  closedText,
  dropHint,
  KIND_TEXT,
  KINDS,
  NEEDS,
  type OrderKind,
  orderError,
  orderText,
  pullSizes,
  resizeTarget,
  STATUS_TEXT,
} from "./workOrders";
import { Skeleton } from "./Skeleton";

function SessionButton({ saved, auth }: { saved: Saved | null; auth: Auth }) {
  if (!saved || saved.ok || !saved.session) return null;
  return (
    <>
      {" "}
      <button type="button" onClick={() => signOut(auth)}>
        Sign in again
      </button>
    </>
  );
}

function SizeSelect({
  id,
  label,
  sizes,
  value,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  sizes: number[];
  value: number | null;
  hint?: string;
  onChange: (value: number | null) => void;
}) {
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        data-testid={id}
        value={value ?? ""}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">Choose a size</option>
        {sizes.map((size) => (
          <option key={size} value={size}>
            {galText(size)}
          </option>
        ))}
      </select>
      {hint && (
        <p id={`${id}-hint`} className="hint" data-testid={`${id}-hint`}>
          {hint}
        </p>
      )}
    </>
  );
}

const KEEP_IN_VIEW_MS = 10_000;
const USER_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];

/** Keep the element in view while the sections above it load, until the user acts. */
function keepInView(element: HTMLElement) {
  const place = () => element.scrollIntoView({ block: "center" });
  const watch = new ResizeObserver(place);
  const stop = () => {
    watch.disconnect();
    clearTimeout(timer);
    for (const name of USER_EVENTS) window.removeEventListener(name, stop);
  };
  const timer = setTimeout(stop, KEEP_IN_VIEW_MS);
  for (const name of USER_EVENTS) window.addEventListener(name, stop, { passive: true });
  watch.observe(document.body);
  return stop;
}

export function WorkOrderForm({
  account,
  auth,
  user,
  quiet,
  onSaved,
  startNote,
}: {
  account: api.AccountDetail;
  auth: Auth;
  user: User;
  quiet: boolean;
  onSaved: (next: api.AccountDetail) => void;
  startNote?: { text: string; key: number } | null;
}) {
  const [resize] = useState(() => resizeTarget(location.search, account.container_sizes));
  const [kind, setKind] = useState<OrderKind>(resize === null ? "swap" : "resize");
  const [pull, setPull] = useState<number | null>(resize !== null && account.containers.length === 1 ? account.containers[0].size_gal : null);
  const [drop, setDrop] = useState<number | null>(resize);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const needs = NEEDS[kind];
  useEffect(() => {
    const title = document.getElementById("order-form-title");
    if (resize === null || !title) return;
    title.focus({ preventScroll: true });
    return keepInView(title);
  }, [resize]);
  useEffect(() => {
    if (!startNote) return;
    setNote(startNote.text);
    entryId.current = null;
    setSaved(null);
    const kindSelect = document.getElementById("order-kind");
    kindSelect?.scrollIntoView({ block: "center" });
    kindSelect?.focus({ preventScroll: true });
  }, [startNote]);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const from = needs.pull ? pull : null;
    const to = needs.drop ? drop : null;
    const problem = orderError(kind, from, to, note);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.OrderEntry = { id: entryId.current, kind, from_size: from, to_size: to, note: note.trim() };
      const next = await api.openWorkOrder(await user.getIdToken(), account.id, entry);
      entryId.current = null;
      setNote("");
      setPull(null);
      setDrop(null);
      onSaved(next);
      const sizes = orderText(entry);
      setSaved({ ok: true, text: `Saved: ${KIND_TEXT[kind]}${sizes ? `. ${sizes}` : ""}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The work order"));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => status.current?.focus());
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  return (
    <form onSubmit={submit} noValidate data-testid="order-form" aria-labelledby="order-form-title">
      <h3 id="order-form-title" tabIndex={-1}>
        Open a work order
      </h3>
      <label htmlFor="order-kind">Work</label>
      <select id="order-kind" data-testid="order-kind" value={kind} onChange={(e) => edit(() => setKind(e.target.value as OrderKind))}>
        {KINDS.map((value) => (
          <option key={value} value={value}>
            {KIND_TEXT[value]}
          </option>
        ))}
      </select>
      {needs.pull && (
        <SizeSelect
          id="order-pull"
          label="Container to pull"
          sizes={pullSizes(
            account.containers.map((c) => c.size_gal),
            account.container_sizes,
          )}
          value={pull}
          onChange={(v) => edit(() => setPull(v))}
        />
      )}
      {needs.drop && (
        <SizeSelect
          id="order-drop"
          label="Size to drop"
          sizes={account.container_sizes}
          value={drop}
          hint={dropHint(account.projection, account.plan !== null)}
          onChange={(v) => edit(() => setDrop(v))}
        />
      )}
      <label htmlFor="order-note">Note (optional)</label>
      <textarea id="order-note" data-testid="order-note" rows={2} value={note} onChange={(e) => edit(() => setNote(e.target.value))} />
      {fieldError && (
        <p role="alert" className="error" data-testid="order-error">
          {fieldError}
        </p>
      )}
      <div className="actions">
        <button className={quiet ? undefined : "primary"} data-testid="save-order" disabled={saving}>
          {saving ? "Saving…" : "Open the work order"}
        </button>
      </div>
      <p ref={status} tabIndex={-1} role="status" data-testid="order-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        <SessionButton saved={saved} auth={auth} />
      </p>
    </form>
  );
}

function CancelForm({
  order,
  auth,
  user,
  onDone,
  onClose,
}: {
  order: api.WorkOrder;
  auth: Auth;
  user: User;
  onDone: () => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const stepId = useRef<string | null>(null);
  const first = useRef<HTMLTextAreaElement>(null);
  useEffect(() => first.current?.focus(), []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = cancelError(note);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    stepId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      await api.stepWorkOrder(await user.getIdToken(), order.id, { id: stepId.current, kind: "cancel", note: note.trim() });
      onDone();
    } catch (e) {
      setSaved(saveFailure(e, "The cancel"));
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const id = `cancel-${order.id}`;
  return (
    <form onSubmit={submit} noValidate className="step-form" data-testid="order-cancel-form">
      <label htmlFor={id}>Reason for the cancel</label>
      <textarea
        ref={first}
        id={id}
        data-testid="order-cancel-note"
        rows={2}
       
        value={note}
        aria-invalid={Boolean(fieldError) || undefined}
        onChange={(e) => {
          setNote(e.target.value);
          stepId.current = null;
          setSaved(null);
        }}
      />
      {fieldError && (
        <p role="alert" className="error" data-testid="order-cancel-error">
          {fieldError}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-cancel" disabled={saving}>
          {saving ? "Saving…" : "Cancel the order"}
        </button>
        <button type="button" data-testid="keep-order" onClick={onClose}>
          Keep the order
        </button>
      </div>
      {saved && !fieldError && (
        <p role="alert" className="error">
          {saved.text}
          <SessionButton saved={saved} auth={auth} />
        </p>
      )}
    </form>
  );
}

export function WorkOrderCard({
  order,
  org,
  auth,
  user,
  showAccount,
  span,
  onChanged,
}: {
  order: api.WorkOrder;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  showAccount: string | null;
  span: number;
  onChanged: (text: string) => void;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [dropSerial, setDropSerial] = useState("");
  const [pullSerial, setPullSerial] = useState("");
  const doneId = useRef<string | null>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const doneButton = useRef<HTMLButtonElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);
  const closed = closedText(order);
  const markDone = async () => {
    doneId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      await api.stepWorkOrder(await user.getIdToken(), order.id, {
        id: doneId.current,
        kind: "done",
        note: "",
        drop_serial: dropSerial.trim() || null,
        pull_serial: pullSerial.trim() || null,
      });
      setConfirming(false);
      onChanged(`Done: ${KIND_TEXT[order.kind]}.`);
    } catch (e) {
      setSaved(saveFailure(e, "The step"));
    } finally {
      setSaving(false);
    }
  };
  const step = order.status === "open" && order.can_edit && (cancelling || confirming);
  return (
    <tbody data-testid="order-row" data-day={callDay(order.opened_at, org.time_zone)} className={`item order-${order.status}`}>
      <tr>
        <th scope="row">
          {KIND_TEXT[order.kind]}
          {order.note && (
            <span className="sub clip" title={order.note}>
              {order.note}
            </span>
          )}
        </th>
        <td data-label="State" title={closed || undefined}>
          <span data-testid="order-state">{STATUS_TEXT[order.status]}</span>
          {closed && (
            <span className="sub desk-hide" data-testid="order-closed">
              {closed}
            </span>
          )}
        </td>
        {showAccount && (
          <td data-label="Account">
            <a href={showAccount} onClick={go} data-testid="order-account">
              {order.account_number} {order.account_name}
            </a>
          </td>
        )}
        <td className="num" data-label="Drop">
          {order.to_size === null ? "–" : galText(order.to_size)}
        </td>
        <td className="num" data-label="Pull">
          {order.from_size === null ? "–" : galText(order.from_size)}
        </td>
        <td
          data-label="Opened"
          title={`${callTime(order.opened_at, org.time_zone)}${
            order.status === "open" ? "" : `. ${STATUS_TEXT[order.status]} ${callTime(order.updated_at, org.time_zone)}`
          }`}
        >
          {shortTime(order.opened_at, org.time_zone)}
          {order.status !== "open" && (
            <span className="sub desk-hide">
              {STATUS_TEXT[order.status]} {callTime(order.updated_at, org.time_zone)}
            </span>
          )}
        </td>
        <td data-label="Opened by" title={order.opened_by ? emailName(order.opened_by) : undefined}>
          {order.opened_by ? emailName(order.opened_by) : "–"}
        </td>
        <td data-label="" className="row-actions">
          {order.status === "open" && order.can_edit && !step && (
            <span className="actions">
              <button ref={doneButton} type="button" data-testid="order-done" onClick={() => setConfirming(true)}>
                Done…
              </button>
              <button ref={cancelButton} type="button" data-testid="order-cancel" onClick={() => setCancelling(true)}>
                Cancel…
              </button>
            </span>
          )}
        </td>
      </tr>
      {(step || (saved && !saved.ok)) && (
        <tr className="detail-row">
          <td colSpan={span} data-label="">
            {step &&
              (cancelling ? (
                <CancelForm
                  order={order}
                  auth={auth}
                  user={user}
                  onClose={() => {
                    setCancelling(false);
                    requestAnimationFrame(() => cancelButton.current?.focus());
                  }}
                  onDone={() => {
                    setCancelling(false);
                    onChanged(`Cancelled: ${KIND_TEXT[order.kind]}.`);
                  }}
                />
              ) : (
                <div className="step-form" data-testid="order-done-form">
                  <p>Mark this work order done? You cannot change it after this.</p>
                  {NEEDS[order.kind].drop && (
                    <p>
                      <label htmlFor={`drop-${order.id}`}>Serial of the container you dropped (optional)</label>
                      <input id={`drop-${order.id}`} data-testid="order-drop-serial" value={dropSerial} maxLength={40} autoComplete="off" onChange={(e) => setDropSerial(e.target.value)} />
                    </p>
                  )}
                  {NEEDS[order.kind].pull && (
                    <p>
                      <label htmlFor={`pull-${order.id}`}>Serial of the container you pulled (optional)</label>
                      <input id={`pull-${order.id}`} data-testid="order-pull-serial" value={pullSerial} maxLength={40} autoComplete="off" onChange={(e) => setPullSerial(e.target.value)} />
                    </p>
                  )}
                  <p className="actions">
                    <button
                      ref={confirmButton}
                      type="button"
                      className="primary"
                      data-testid="order-done-confirm"
                      disabled={saving}
                      onClick={markDone}
                    >
                      {saving ? "Saving…" : "Mark it done"}
                    </button>
                    <button
                      type="button"
                      data-testid="order-done-keep"
                      onClick={() => {
                        setConfirming(false);
                        requestAnimationFrame(() => doneButton.current?.focus());
                      }}
                    >
                      Keep it open
                    </button>
                  </p>
                </div>
              ))}
            {saved && !saved.ok && (
              <p role="alert" className="error">
                {saved.text}
                <SessionButton saved={saved} auth={auth} />
              </p>
            )}
          </td>
        </tr>
      )}
    </tbody>
  );
}

/** The work orders as one dense table; the account column shows only outside an account page. */
export function WorkOrderTable({
  orders,
  base,
  org,
  auth,
  user,
  param,
  onChanged,
}: {
  orders: api.WorkOrder[];
  base: string | null;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  param: string;
  onChanged: (text: string) => void;
}) {
  const columns = base ? ORDER_COLUMNS : ORDER_COLUMNS.filter((column) => column.key !== "account");
  const [sort, setSort] = useSort(param, columns);
  const span = columns.length + 1;
  return (
    <>
      <SortSelect id={`order-${param}-select`} columns={columns} sort={sort} onSort={setSort} fallback="Newest first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Work orders">
        <table className="list orders-table" data-testid="order-table" data-cols={`order-${param}-select`}>
          <thead>
            <tr>
              {columns.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
              <th scope="col">
                <span className="visually-hidden">Steps</span>
              </th>
            </tr>
          </thead>
          {sortRows(orders, columns, sort).map((order) => (
            <WorkOrderCard
              key={order.id}
              order={order}
              org={org}
              auth={auth}
              user={user}
              showAccount={base && `${base}/accounts/${order.account_id}`}
              span={span}
              onChanged={onChanged}
            />
          ))}
        </table>
      </div>
    </>
  );
}

type OrderLoad = { orders: api.WorkOrder[]; pick_list: api.PickCount[] } | "server" | "network" | "session" | undefined;

/** Spec F-INV-3: the open orders, and the containers to load for them. */
export function WorkOrderList({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [closed, setClosedState] = useState(() => new URLSearchParams(location.search).get("view") === "closed");
  const [found, setFound] = useState<OrderLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [note, setNote] = useState("");
  const noteLine = useRef<HTMLParagraphElement>(null);
  const setClosed = (next: boolean) => {
    setClosedState(next);
    setNote("");
    const url = new URL(location.href);
    if (next) url.searchParams.set("view", "closed");
    else url.searchParams.delete("view");
    history.replaceState(history.state, "", url);
  };
  useTitle(`Work orders · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.workOrders(token, closed ? "closed" : "open"))
      .then((list) => live && setFound(list))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, closed, attempt, canView]);
  if (canView === false) {
    return (
      <main data-testid="orders-hidden">
        <h1 tabIndex={-1}>Work orders</h1>
        <p>Your role does not show work orders.</p>
      </main>
    );
  }
  return (
    <main className="issues-page wide" data-testid="orders-page">
      <h1 tabIndex={-1}>Work orders</h1>
      <div className="tabs" role="group" aria-label="Which work orders">
        <button type="button" aria-pressed={!closed} data-testid="orders-open" onClick={() => setClosed(false)}>
          Open
        </button>
        <button type="button" aria-pressed={closed} data-testid="orders-closed" onClick={() => setClosed(true)}>
          Done or cancelled
        </button>
      </div>
      <p ref={noteLine} tabIndex={-1} role="status" data-testid="orders-note">
        {note}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} rows={8} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : (
        <>
          {!closed && (
            <section aria-labelledby="pick-list">
              <h2 id="pick-list">Pick list</h2>
              {found.pick_list.length ? (
                <ul className="containers" data-testid="pick-list">
                  {found.pick_list.map((row) => (
                    <li key={row.size_gal}>
                      {row.count} × {galText(row.size_gal)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p data-testid="pick-list-none">No containers to load.</p>
              )}
            </section>
          )}
          {found.orders.length === 0 ? (
            <p data-testid="orders-none">{closed ? "No done or cancelled work orders." : "No open work orders."}</p>
          ) : (
            <WorkOrderTable
              orders={found.orders}
              base={base}
              org={org}
              auth={auth}
              user={user}
              param="sort"
              onChanged={(text) => {
                setNote(text);
                setAttempt((n) => n + 1);
                requestAnimationFrame(() => noteLine.current?.focus());
              }}
            />
          )}
        </>
      )}
    </main>
  );
}
